// frontend/src/App.tsx - OPTIMIZED WITH LAZY LOADING FOR PERFORMANCE
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { lazy, Suspense } from 'react';
import { useAuthStore } from '@/stores/auth';
import { useAuthInit } from '@/hooks/useAuthInit';
import DashboardLoadingSkeleton from '@/components/common/DashboardLoadingSkeleton';

// Critical pages loaded immediately
import LandingPage from '@/pages/LandingPage';
const LoginPage = lazy(() => import('@/pages/LoginPageWithPassword'));
const VerifyPage = lazy(() => import('@/pages/VerifyPage'));

// Lazy load heavy pages to reduce bundle size
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const InterviewHub = lazy(() => import('@/pages/InterviewHub'));
const InterviewPage = lazy(() => import('@/interview/pages/InterviewPage'));
const EnhancedInterviewPage = lazy(() => import('@/interview/pages/EnhancedInterviewPage'));
const SpecializedInterviewPage = lazy(() => import('./interview/pages/SpecializedInterviewPage'));
const ResumePage = lazy(() => import('@/resume/pages/ResumePage'));
const ResumeViewPage = lazy(() => import('@/resume/pages/ResumeViewPage'));
const ConversationalResumeBuilder = lazy(() => import('@/resume/pages/ConversationalResumeBuilder'));
const SimpleResumePage = lazy(() => import('@/resume/pages/SimpleResumePage'));
const EnhancedResumeBuilder = lazy(() => import('@/resume/pages/EnhancedResumeBuilder'));
const EnhancedResumePage = lazy(() => import('@/resume/pages/EnhancedResumePage'));
const AnalyticsPage = lazy(() => import('@/pages/AnalyticsPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));

// Dynamic Interview Pages
const InterviewReadinessScore = lazy(() => import('@/components/interview/InterviewReadinessScore'));
const ReadinessAssessmentSetup = lazy(() => import('@/pages/interview/ReadinessAssessmentSetup'));
const ReadinessAssessment = lazy(() => import('@/pages/interview/ReadinessAssessment'));
const DynamicInterviewSetup = lazy(() => import('@/components/interview/DynamicInterviewSetup'));
const DynamicInterviewSession = lazy(() => import('@/components/interview/DynamicInterviewSession'));
const InterviewResults = lazy(() => import('@/components/interview/InterviewResults'));

// Avatar Demo Page - DISABLED FOR LAUNCH
// import AvatarDemo from '@/pages/AvatarDemo';
// import AvatarManager from '@/pages/AvatarManager';
// import VideoInterviewPage from '@/pages/VideoInterviewPage';
// import VideoInterviewResults from '@/pages/VideoInterviewResults';
// import EnhancedAvatarInterview from '@/components/avatar/EnhancedAvatarInterview';

// Career Platform Pages
const CareerDashboard = lazy(() => import('@/pages/career/CareerDashboard'));
const CareerAssessment = lazy(() => import('@/pages/career/CareerAssessment'));
const SkillGapAnalysis = lazy(() => import('@/pages/career/SkillGapAnalysis'));
const JobTracker = lazy(() => import('@/pages/career/JobTracker'));
const LearningPath = lazy(() => import('@/pages/learning/LearningPath'));
const NetworkHub = lazy(() => import('@/pages/networking/NetworkHub'));

// Resources Pages
const InterviewResourcesPage = lazy(() => import('@/resources/pages/InterviewResourcesPage'));

// Layouts
import AuthLayout from '@/layouts/AuthLayout';
import AppLayout from '@/layouts/AppLayout';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
});

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/" replace />;
}

export default function App() {
  // Initialize auth state from localStorage on app mount
  const { isInitializing } = useAuthInit();
  
  // Show loading screen while auth is initializing
  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
        
        <Suspense fallback={<DashboardLoadingSkeleton />}>
          <Routes>
            {/* Public Landing Page */}
            <Route path="/" element={<LandingPage />} />
            
            {/* Auth Routes */}
            <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/verify" element={<VerifyPage />} />
          </Route>
          
          {/* App Routes */}
          <Route
            element={
              <PrivateRoute>
                <AppLayout />
              </PrivateRoute>
            }
          >
            <Route path="/app" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={
              <Suspense fallback={<DashboardLoadingSkeleton />}>
                <DashboardPage />
              </Suspense>
            } />
            <Route path="/resume" element={
              <Suspense fallback={<DashboardLoadingSkeleton />}>
                <ResumePage />
              </Suspense>
            } />
            <Route path="/resume/view/:id" element={
              <Suspense fallback={<DashboardLoadingSkeleton />}>
                <ResumeViewPage />
              </Suspense>
            } />
            
            {/* Interview Routes */}
            <Route path="/interview" element={
              <Suspense fallback={<DashboardLoadingSkeleton />}>
                <InterviewHub />
              </Suspense>
            } />
            <Route path="/interview/standard" element={
              <Suspense fallback={<DashboardLoadingSkeleton />}>
                <InterviewPage />
              </Suspense>
            } />
            <Route path="/interview/enhanced" element={
              <Suspense fallback={<DashboardLoadingSkeleton />}>
                <EnhancedInterviewPage />
              </Suspense>
            } />
            <Route path="/interview/specialized" element={
              <Suspense fallback={<DashboardLoadingSkeleton />}>
                <SpecializedInterviewPage />
              </Suspense>
            } />
            {/* Avatar and Video Interview Routes - DISABLED FOR LAUNCH */}
            {/* <Route path="/interview/avatar-demo" element={<AvatarDemo />} /> */}
            {/* <Route path="/interview/avatar-manager" element={<AvatarManager />} /> */}
            {/* <Route path="/interview/video" element={<VideoInterviewPage />} /> */}
            {/* <Route path="/interview/results/:sessionId" element={<VideoInterviewResults />} /> */}
            {/* <Route path="/interview/enhanced-avatar" element={
              <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-7xl mx-auto">
                  <EnhancedAvatarInterview />
                </div>
              </div>
            } /> */}
            <Route path="/interview/readiness-score" element={<ReadinessAssessmentSetup />} />
            <Route path="/interview/readiness-assessment" element={<ReadinessAssessment />} />
            <Route path="/interview/readiness-score/results" element={<InterviewReadinessScore />} />
            <Route path="/interview/readiness-score/results/:sessionId" element={<InterviewReadinessScore />} />
            <Route path="/interview/dynamic" element={<DynamicInterviewSetup />} />
            <Route path="/interview/dynamic/:sessionId" element={<DynamicInterviewSession />} />
            <Route path="/interview/results/:sessionId" element={<InterviewResults />} />
            
            <Route path="/analytics" element={
              <Suspense fallback={<DashboardLoadingSkeleton />}>
                <AnalyticsPage />
              </Suspense>
            } />
            <Route path="/resume/conversational" element={
              <Suspense fallback={<DashboardLoadingSkeleton />}>
                <ConversationalResumeBuilder />
              </Suspense>
            } />
            <Route path="/resume/simple" element={
              <Suspense fallback={<DashboardLoadingSkeleton />}>
                <SimpleResumePage />
              </Suspense>
            } />
            <Route path="/resume-builder/enhanced" element={
              <Suspense fallback={<DashboardLoadingSkeleton />}>
                <EnhancedResumeBuilder />
              </Suspense>
            } />
            <Route path="/resume/enhanced" element={
              <Suspense fallback={<DashboardLoadingSkeleton />}>
                <EnhancedResumePage />
              </Suspense>
            } />
            <Route path="/profile" element={
              <Suspense fallback={<DashboardLoadingSkeleton />}>
                <ProfilePage />
              </Suspense>
            } />
            
            {/* Career Platform Routes */}
            <Route path="/career" element={<CareerDashboard />} />
            <Route path="/career/assessment/:type?" element={<CareerAssessment />} />
            <Route path="/career/skills" element={<SkillGapAnalysis />} />
            <Route path="/career/applications" element={<JobTracker />} />
            <Route path="/learning" element={<LearningPath />} />
            <Route path="/network" element={<NetworkHub />} />
            
            {/* Resources Routes */}
            <Route path="/resources/interview" element={<InterviewResourcesPage />} />
          </Route>
        </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}